import { useCallback, useEffect, useRef, useState } from "react";
import notification from "../../../../helpers/notification";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import { EMPTY_JOURNEY_FORM, buildJourneyPayload, stopHasPoint, validateJourney, validateLeader, validateStopAreas, validateTempleName } from "./journeyForm";
import submitTspJourney from "./submitTspJourney";
import TspApiService from "../../api/tspApiService";
import { uploadMedia } from "../media-gallery/useTspMediaData";
import { CITY_TABS, emptyCityDetails, hasCityDetails, validateCityDetails } from "./addJourneyCityDetails";
import saveJourneyCityDetails from "./saveJourneyCityDetails";
import { areasToText, parseAreasText } from "./mapPickModel";
import { placeLabel, samePlace, tidyName } from "../../utils/tspPlace";
import { formatCoordinate } from "../../utils/tspCoordinates";
import { chronologicalIndex, journeyDatesFrom, validatePlaceDate } from "./builder/journeyBuilderModel";

const PENDING_MAP_POINT_ERROR = "The location selected on the map is not added yet. Complete it in Add Journey Place, choose its visit date and select Add Place.";

// validateCityDetails' tab names -> the place card's tab keys.
const TAB_KEY = {
  [CITY_TABS.visit]: "visit",
  [CITY_TABS.books]: "books",
  [CITY_TABS.accommodation]: "accommodation",
  [CITY_TABS.mediaAdvice]: "mediaAdvice",
  [CITY_TABS.response]: "response",
};
const byTabKey = (errors) => Object.fromEntries(Object.entries(errors).map(([tab, message]) => [TAB_KEY[tab] || tab, message]));

// Form state + actions for Add Journey (the Journey Builder in create mode).
// `canManage` is the caller's TSP permission — the hook refuses to submit
// without it even if a button were somehow enabled; `onCreated(journey)` runs
// after (and only after) the server has saved it — already started (In
// Progress). `temples` are the signed-in user's own (API form options): the
// journey is for their (primary) temple; it is never typed.
//
// The journey is its places: each added from the State / District / Taluka-Town
// picker WITH its visit date, as a card (in date order). A card's sections
// (Visit Info, Book Distribution, Accommodation, Media & Advice, Public
// Response) are filled in on the card itself and held here
// until Create Journey; the journey's Start / End Date are the first and last
// place dates. Nothing typed is ever cleared or lost: a failed save leaves every
// field as it was and shows the server's reason, so it can be fixed and retried.
//
// A point picked on the map (Map -> Create Journey) enters through
// applyMapSelection: its place fills the picker (`pickerPreset`) as far as it is
// known, and the point itself (with its areas) waits as the PENDING map point
// that the next place added takes with it.
//
// The place details are saved right AFTER the server has created the journey
// (saveJourneyCityDetails), through the same endpoints the journey page uses.
// The journey is never created twice: if some detail is refused, `detailsSave`
// lists what was and was not saved, and retryDetails() sends only what is missing.
export default function useTspAddJourneyForm({ canManage, temples = [], onCreated }) {
  const [values, setValues] = useState(EMPTY_JOURNEY_FORM);
  const [pendingMapPoint, setPendingMapPoint] = useState(null);
  const [pickerPreset, setPickerPreset] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  // { [stopId]: details } — only places whose sections were used have an entry.
  const [cityDetails, setCityDetails] = useState({});
  // { [stopId]: { [tabKey]: message } } from validation at Create Journey.
  const [detailErrors, setDetailErrors] = useState({});
  // Which place cards are open, and each card's open section: { [stopId]: tabKey | null }.
  const [openCards, setOpenCards] = useState({});
  const [activeTabs, setActiveTabs] = useState({});
  // After the journey is created: { journey, phase: "saving" | "review", results }.
  const [detailsSave, setDetailsSave] = useState(null);
  const stopCounter = useRef(0);
  const mounted = useRef(true);
  // Set synchronously: a double click delivers both clicks before React
  // re-renders, so the `submitting` state alone would let two requests through.
  const inFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // The journey is for the user's own temple — their primary one when they have
  // several (the first the API lists), as the server would choose it.
  const accountTemple = temples.length ? temples[0] : "";
  useEffect(() => {
    if (accountTemple) setValues((previous) => (previous.templeName ? previous : { ...previous, templeName: accountTemple }));
  }, [accountTemple]);

  const clearError = (key) =>
    setErrors((previous) => {
      if (!previous[key]) return previous;
      const { [key]: removed, ...rest } = previous;
      return rest;
    });

  // The leader field: { name, userId } — userId "" for a name typed by hand.
  const setLeader = useCallback(({ name, userId }) => {
    setValues((previous) => ({ ...previous, leaderName: name, leaderUserId: userId ? String(userId) : "" }));
    setErrors((previous) => {
      if (!previous.leader) return previous;
      const { leader: removed, ...rest } = previous;
      return rest;
    });
  }, []);

  const range = journeyDatesFrom(values.cities);

  // `place` comes from the State -> District -> City picker (utils/tspPlace.js),
  // `date` is its visit date. In the new flow, Add Place creates the journey
  // immediately in the database and navigates to the builder/editor.
  const addCity = async (place, date) => {
    if (!canManage) {
      const msg = "You do not have permission to save journeys.";
      notification("error", msg);
      return { ok: false, message: msg };
    }
    if (!place || !place.stateId || !place.districtId || !tidyName(place.city)) return { ok: false, message: "Select a state, a district and a taluka / town / municipality first." };
    const dateProblem = validatePlaceDate(date);
    if (dateProblem) return { ok: false, message: dateProblem };

    const templeError = validateTempleName(values.templeName, temples);
    if (templeError) {
      setErrors((previous) => ({ ...previous, templeName: templeError }));
      return { ok: false, message: templeError };
    }
    const leaderProblem = validateLeader({ leaderName: values.leaderName, leaderUserId: values.leaderUserId });
    if (leaderProblem) {
      setErrors((previous) => ({ ...previous, leader: leaderProblem }));
      return { ok: false, message: leaderProblem };
    }

    let stop = { ...place, city: tidyName(place.city), plannedDate: date, areas: place.areas || [] };
    const takesMapPoint = Boolean(pendingMapPoint && !stopHasPoint(stop));
    if (takesMapPoint) {
      const areas = parseAreasText(pendingMapPoint.areasText);
      const areasError = validateStopAreas(areas);
      if (areasError) return { ok: false, message: areasError };
      stop = { ...stop, latitude: pendingMapPoint.latitude, longitude: pendingMapPoint.longitude, areas };
    }

    if (inFlight.current || submitting) return { ok: false, message: "" };

    inFlight.current = true;
    setSubmitting(true);
    setSubmitError("");
    try {
      const payload = buildJourneyPayload({
        ...values,
        cities: [stop],
      });
      const { journey } = await submitTspJourney(payload);
      notification("success", `Journey “${journey.journey_name}” created.`);
      if (takesMapPoint) {
        setPendingMapPoint(null);
        setPickerPreset(null);
      }
      setValues((previous) => ({ ...previous, cities: [stop] }));
      setOpenCards({ [stop.id]: true });
      setActiveTabs({ [stop.id]: "books" });
      if (mounted.current) {
        onCreated?.(journey);
      }
      return { ok: true };
    } catch (error) {
      const details = error?.response?.data?.details;
      if (error?.response?.status === 409 && details?.code === "DUPLICATE_SUBMISSION" && details.journey_id) {
        notification("info", "This journey was already created — opening it.");
        if (mounted.current) onCreated?.({ id: details.journey_id });
        return { ok: true };
      }
      const message = tspErrorMessage(error, "Could not save the journey. Please try again.");
      notification("error", message);
      if (mounted.current) setSubmitError(message);
      return { ok: false, message };
    } finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  // Map -> Create Journey. `selection` = mapPickModel.mapSelectionForForm():
  // { point, place, areas }. Replaces any earlier one.
  const applyMapSelection = (selection) => {
    if (!selection || !selection.point) return;
    const { point, place, areas } = selection;
    setPendingMapPoint({ latitude: point.latitude, longitude: point.longitude, areasText: areasToText(areas) });
    stopCounter.current += 1;
    setPickerPreset(place ? { key: `map-${stopCounter.current}`, ...place } : null);
    if (place && place.districtId) setAnnouncement(`${placeLabel(place)} from the map is filled in. Choose its visit date, then add it.`);
  };

  // Takes a place with a map point back into the picker (to change its place);
  // its point, areas and visit date wait there until it is added again.
  const editStop = (id) => {
    const stop = values.cities.find((candidate) => candidate.id === id);
    if (!stop || !stopHasPoint(stop)) return;
    if (hasCityDetails(cityDetails[id]) && !window.confirm(`Changing the place of ${placeLabel(stop)} clears the details entered for it. Continue?`)) return;
    setValues((previous) => ({ ...previous, cities: previous.cities.filter((candidate) => candidate.id !== id) }));
    setCityDetails(({ [id]: dropped, ...rest }) => rest);
    setDetailErrors(({ [id]: dropped, ...rest }) => rest);
    setPendingMapPoint({ latitude: stop.latitude, longitude: stop.longitude, areasText: areasToText(stop.areas) });
    stopCounter.current += 1;
    setPickerPreset({ key: `edit-${stopCounter.current}`, stateId: stop.stateId, state: stop.state, districtId: stop.districtId, district: stop.district, cityId: stop.cityId, city: stop.city, kind: stop.kind, plannedDate: stop.plannedDate });
    setAnnouncement(`${placeLabel(stop)} moved back to the place picker to be changed (map location ${formatCoordinate(stop.latitude)}, ${formatCoordinate(stop.longitude)} kept).`);
  };

  const removeCity = (id) => {
    const removed = values.cities.find((stop) => stop.id === id);
    if (removed && hasCityDetails(cityDetails[id]) && !window.confirm(`Remove ${placeLabel(removed)} and the details entered for it?`)) return;
    setValues((previous) => ({ ...previous, cities: previous.cities.filter((stop) => stop.id !== id) }));
    setCityDetails(({ [id]: dropped, ...rest }) => rest);
    setDetailErrors(({ [id]: dropped, ...rest }) => rest);
    if (removed) setAnnouncement(`${placeLabel(removed)} removed from the journey.`);
  };

  const moveCity = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= values.cities.length) return;
    const next = [...values.cities];
    [next[index], next[target]] = [next[target], next[index]];
    setValues((previous) => ({ ...previous, cities: next }));
    setAnnouncement(`${placeLabel(next[target])} moved to position ${target + 1} of ${next.length}.`);
  };

  // A place's visit date, changed on its card.
  const changeStop = (id, patch) => {
    setValues((previous) => ({ ...previous, cities: previous.cities.map((stop) => (stop.id === id ? { ...stop, ...patch } : stop)) }));
  };
  const setPlaceDate = (id, date) => {
    const problem = validatePlaceDate(date);
    if (problem) return Promise.resolve({ ok: false, message: problem });
    changeStop(id, { plannedDate: date });
    setErrors((previous) => {
      if (!previous.placeDates || !previous.placeDates[id]) return previous;
      const { [id]: fixed, ...placeDates } = previous.placeDates;
      return { ...previous, placeDates };
    });
    return Promise.resolve({ ok: true });
  };

  const setPlaceVisitState = (id, visitState) => {
    changeStop(id, { visitState });
  };

  // ---- per-place details (the card sections)

  const detailsOf = (stopId) => cityDetails[stopId] || emptyCityDetails();

  const changeCityDetails = useCallback((stopId, updater) => {
    setCityDetails((previous) => ({ ...previous, [stopId]: updater(previous[stopId] || emptyCityDetails()) }));
    setDetailErrors((previous) => {
      if (!previous[stopId]) return previous;
      const { [stopId]: cleared, ...rest } = previous;
      return rest;
    });
  }, []);

  const toggleCard = (stopId) => setOpenCards((previous) => ({ ...previous, [stopId]: !previous[stopId] }));
  const setCardTab = (stopId, tab) => setActiveTabs((previous) => ({ ...previous, [stopId]: tab }));

  // Saves the details of a created journey; resumes from `previous` on a retry.
  const saveDetails = async (journey, previous = {}) => {
    setDetailsSave({ journey, phase: "saving", results: previous });
    const { results, allDone } = await saveJourneyCityDetails({
      journey,
      stops: values.cities,
      detailsByStop: cityDetails,
      range,
      api: TspApiService,
      uploadMedia,
      previous,
      onProgress: (progress) => mounted.current && setDetailsSave({ journey, phase: "saving", results: progress }),
    });
    if (!mounted.current) return;
    if (allDone) {
      notification("success", "All place details were saved with the journey.");
      setDetailsSave(null);
      onCreated?.(journey);
    } else {
      notification("warning", "The journey was created, but some place details could not be saved.");
      setDetailsSave({ journey, phase: "review", results });
    }
  };

  const retryDetails = () => {
    if (!detailsSave || detailsSave.phase !== "review") return;
    saveDetails(detailsSave.journey, detailsSave.results);
  };
  const openCreatedJourney = () => {
    if (detailsSave) onCreated?.(detailsSave.journey);
  };

  const submit = async () => {
    if (!canManage) {
      notification("error", "You do not have permission to save journeys.");
      return;
    }
    if (submitting || inFlight.current || detailsSave) return; // created already: never twice

    const found = validateJourney(values, { temples });
    // A map point never silently disappears from a saved journey.
    if (pendingMapPoint) found.cities = PENDING_MAP_POINT_ERROR;
    setErrors(found);
    setSubmitError("");
    if (found.templeName || found.leader || found.cities) {
      notification("warning", "Please fix the highlighted fields.");
      const firstDateless = values.cities.find((stop) => found.placeDates && found.placeDates[stop.id]);
      if (firstDateless) setOpenCards((previous) => ({ ...previous, [firstDateless.id]: true }));
      const target = found.leader ? "tsp-journey-leader" : firstDateless ? `jb-${firstDateless.id}-name` : "tsp-journey-route-state";
      document.getElementById(target)?.focus?.();
      return;
    }
    // Every place's details are checked before anything is sent.
    const foundDetails = {};
    values.cities.forEach((stop) => {
      const stopErrors = validateCityDetails(cityDetails[stop.id], range);
      if (Object.keys(stopErrors).length) foundDetails[stop.id] = byTabKey(stopErrors);
    });
    setDetailErrors(foundDetails);
    const firstStop = values.cities.find((stop) => foundDetails[stop.id]);
    if (firstStop) {
      setOpenCards((previous) => ({ ...previous, [firstStop.id]: true }));
      setActiveTabs((previous) => ({ ...previous, [firstStop.id]: Object.keys(foundDetails[firstStop.id])[0] }));
      notification("warning", `Please fix the details of ${placeLabel(firstStop)}.`);
      setAnnouncement(`The details of ${placeLabel(firstStop)} need attention.`);
      return;
    }

    inFlight.current = true;
    setSubmitting(true);
    try {
      const { journey } = await submitTspJourney(buildJourneyPayload(values));
      notification("success", `Journey “${journey.journey_name}” created and started.`);
      if (!mounted.current) return;
      if (values.cities.some((stop) => hasCityDetails(cityDetails[stop.id]))) await saveDetails(journey);
      else onCreated?.(journey);
    } catch (error) {
      const details = error?.response?.data?.details;
      if (error?.response?.status === 409 && details?.code === "DUPLICATE_SUBMISSION" && details.journey_id) {
        // The server already has this journey (a second tab or a retried
        // request): open it rather than offer to create it again.
        notification("info", "This journey was already created — opening it.");
        if (mounted.current) onCreated?.({ id: details.journey_id });
        return;
      }
      const message = tspErrorMessage(error, "Could not save the journey. Please try again.");
      notification("error", message);
      if (mounted.current) setSubmitError(message);
    } finally {
      inFlight.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  // The temple filled in from the account is not something the person entered.
  const untouched = { ...EMPTY_JOURNEY_FORM, templeName: accountTemple };

  return {
    values,
    errors,
    range,
    submitError,
    submitting,
    announcement,
    isDirty: JSON.stringify(values) !== JSON.stringify(untouched) || Boolean(pendingMapPoint) || values.cities.some((stop) => hasCityDetails(cityDetails[stop.id])),
    pendingMapPoint,
    pickerPreset,
    setLeader,
    addCity,
    removeCity,
    moveCity,
    editStop,
    setPlaceDate,
    setPlaceVisitState,
    applyMapSelection,
    submit,
    // place cards
    detailsOf,
    changeCityDetails,
    detailErrors,
    openCards,
    toggleCard,
    activeTabs,
    setCardTab,
    detailsSave,
    retryDetails,
    openCreatedJourney,
  };
}
